import { formatBytes, formatCount, formatDuration } from '@/lib/format';
import type { CellResult } from '@/query/resultStore';

export function CellStatusBar({ result }: { result: CellResult }): JSX.Element | null {
  if (result.status === 'idle') return null;

  const { stats } = result;

  return (
    <div
      className="flex h-6 flex-wrap items-center gap-x-3 border-t border-line bg-surface px-2.5"
      data-testid="cell-status"
    >
      <StatusDot status={result.status} />

      {result.status === 'running' ? (
        <span className="tnum text-[10px] text-muted">
          {result.progressRows > 0 ? `${formatCount(result.progressRows)} rows received` : 'Submitted'}
        </span>
      ) : null}

      {result.status === 'success' && stats ? (
        <>
          <Metric label="ran in" value={formatDuration(stats.executionMs)} />
          <Metric label="rows" value={formatCount(result.totalRows)} />
          <Metric label="scanned" value={formatBytes(stats.bytesScanned)} />
          {stats.bytesSpilled > 0 ? (
            <Metric label="spilled" value={formatBytes(stats.bytesSpilled)} warn />
          ) : null}
          <Metric label="duckling" value={`${stats.ducklingId} · ${stats.tier}`} />
        </>
      ) : null}

      {result.status === 'error' && result.error ? (
        <span className="tnum text-[10px] text-danger">SQLSTATE {result.error.sqlState}</span>
      ) : null}

      {result.status === 'cancelled' ? (
        <span className="text-[10px] text-muted">Cancelled before completion.</span>
      ) : null}
    </div>
  );
}

function StatusDot({ status }: { status: CellResult['status'] }): JSX.Element {
  const color =
    status === 'success'
      ? 'var(--c-ok)'
      : status === 'error'
        ? 'var(--c-danger)'
        : status === 'running'
          ? 'var(--c-local)'
          : 'var(--c-faint)';

  return (
    <span className="flex items-center gap-1.5">
      <span
        className={`h-1.5 w-1.5 rounded-full ${status === 'running' ? 'animate-pulse' : ''}`}
        style={{ background: color }}
        aria-hidden="true"
      />
      <span className="eyebrow">{status}</span>
    </span>
  );
}

function Metric({
  label,
  value,
  warn,
}: {
  label: string;
  value: string;
  warn?: boolean;
}): JSX.Element {
  return (
    <span className="flex items-baseline gap-1">
      <span className="eyebrow">{label}</span>
      <span className={`tnum text-[10px] ${warn ? 'text-remote' : 'text-muted'}`}>{value}</span>
    </span>
  );
}
